/* ===================================================================
   useRequestsSummary() -- this-period review-request funnel for the
   Dashboard "This month's requests" card.

   Binds to GET /api/v1/requests/summary (engine/api/v1/app.py) through the
   typed api client (src/lib/api.ts). Disabled until a Bearer rr_ token
   exists. `days` sets the look-back window (server default 30, max 365).
   The response's `source` field says whether the numbers came from the
   requests table or the transactions fallback (where delivered_or_opened
   is honestly 0) -- the screen renders that truthfully.
   =================================================================== */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api, type AdminTenantDashboard, type RequestsSummary } from '../lib/api';
import { useApiToken } from './useApiToken';
import { useAdminView } from '../admin/adminViewContext';
import { adminDashboardKey } from './useAdminTenantDashboard';
import { qk } from './queryKeys';

export interface UseRequestsSummaryParams {
  /** Look-back window in days (server default 30). */
  days?: number;
  /** Force-disable even if a token exists. */
  enabled?: boolean;
}

export function useRequestsSummary(
  params: UseRequestsSummaryParams = {},
): UseQueryResult<RequestsSummary, Error> {
  const { days, enabled } = params;
  const hasToken = Boolean(useApiToken());
  const admin = useAdminView();

  // OPTION A: in an operator read-only view, select the request-funnel slice out
  // of the single admin dashboard bundle (one cached fetch shared across panels).
  return useQuery({
    queryKey: admin ? adminDashboardKey(admin.clientId) : qk.requestsSummary(days),
    queryFn: ({ signal }) =>
      admin
        ? (api.getTenantDashboard(admin.clientId, signal) as Promise<unknown>)
        : (api.getRequestsSummary(days, signal) as Promise<unknown>),
    select: admin
      ? (d) => (d as AdminTenantDashboard).requests_summary
      : undefined,
    enabled: (admin ? true : hasToken) && enabled !== false,
    staleTime: 60_000,
  }) as UseQueryResult<RequestsSummary, Error>;
}
